import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import Plot from 'react-plotly.js'
import { TOKENS, FONT_SANS, FONT_MONO } from './OptimiserDesign'

const PALETTE = ['#E74C3C', '#E67E22', '#9B59B6', '#2980B9', '#27AE60', '#F1C40F', '#1ABC9C']
const formatPeriod = (p: number) => `W${String(p).padStart(2, '0')}`
const PLOTLY_CONFIG = { displayModeBar: false, responsive: true }

type Row = Record<string, unknown>
type Mode = 'lost_sales' | 'backorder'
const getStr = (r: Row, k: string) => String(r[k] ?? '')
const getNum = (r: Row, k: string) => Number(r[k] ?? 0)

export const UnmetDemandChart = ({
  unmetRows,
  demandRows,
}: {
  unmetRows: Row[]
  demandRows: Row[]
}) => {
  const [mode, setMode] = useState<Mode>('lost_sales')

  useEffect(() => { setMode('lost_sales') }, [unmetRows])

  const periods = useMemo(() => {
    const ps = new Set<number>()
    unmetRows.forEach((r) => ps.add(getNum(r, 'period')))
    return Array.from(ps).sort((a, b) => a - b)
  }, [unmetRows])

  const locations = useMemo(() => {
    const ls = new Set<string>()
    unmetRows.forEach((r) => ls.add(getStr(r, 'location')))
    return Array.from(ls).sort()
  }, [unmetRows])

  const unmetMap = useMemo(() => {
    const m: Record<string, Record<number, number>> = {}
    unmetRows.forEach((r) => {
      const loc = getStr(r, 'location')
      const t = getNum(r, 'period')
      m[loc] = m[loc] ?? {}
      m[loc][t] = (m[loc][t] ?? 0) + getNum(r, 'value')
    })
    return m
  }, [unmetRows])

  const demandByPeriod = useMemo(() => {
    const m: Record<number, number> = {}
    demandRows.forEach((r) => {
      const t = getNum(r, 'period')
      m[t] = (m[t] ?? 0) + getNum(r, 'value')
    })
    return m
  }, [demandRows])

  const periodLabels = periods.map(formatPeriod)

  const totalUnmetByPeriod = periods.map((t) =>
    locations.reduce((sum, loc) => sum + (unmetMap[loc]?.[t] ?? 0), 0)
  )

  const fillRateByPeriod = periods.map((t, i) => {
    const dem = demandByPeriod[t] ?? 0
    if (!dem) return 1
    return Math.max(0, 1 - (totalUnmetByPeriod[i] ?? 0) / dem)
  })

  const barTraces = locations.map((loc, i) => ({
    type: 'bar' as const,
    name: loc,
    x: periodLabels,
    y: periods.map((t) => unmetMap[loc]?.[t] ?? 0),
    marker: { color: PALETTE[i % PALETTE.length] },
    yaxis: 'y' as const,
  }))

  const fillRateTrace = {
    type: 'scatter' as const, mode: 'lines' as const, name: 'Fill rate',
    x: periodLabels, y: fillRateByPeriod,
    yaxis: 'y2' as const,
    line: { color: '#27AE60', width: 2 },
  }

  if (unmetRows.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 12, color: TOKENS.ink300 }}>
          No unmet demand data
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${TOKENS.line100}`, flexShrink: 0,
                 display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 11.5, fontWeight: 700, color: TOKENS.ink900 }}>
          Unmet Demand
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontFamily: FONT_SANS, fontSize: 10, fontWeight: 700, color: TOKENS.ink400,
                            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Mode
          </Typography>
          {(['lost_sales', 'backorder'] as const).map((m) => (
            <Box
              key={m}
              onClick={() => setMode(m)}
              sx={{
                px: '8px', py: '2px', borderRadius: '100px', fontSize: 10.5, fontFamily: FONT_SANS,
                fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${mode === m ? TOKENS.brandTeal : TOKENS.line300}`,
                color: mode === m ? TOKENS.brandTeal : TOKENS.ink700,
                background: mode === m ? TOKENS.blue50 : 'transparent',
                '&:hover': { borderColor: TOKENS.brandTeal },
              }}
            >
              {m === 'lost_sales' ? 'Lost Sales' : 'Backorder'}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Plot
          data={[...barTraces, fillRateTrace]}
          layout={{
            paper_bgcolor: 'transparent', plot_bgcolor: TOKENS.surface50,
            font: { family: FONT_SANS, size: 10, color: TOKENS.ink700 },
            margin: { l: 44, r: 44, t: 8, b: 44 }, autosize: true, barmode: 'stack',
            legend: { orientation: 'h', y: -0.22, x: 0, font: { size: 9 } },
            xaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200 },
            yaxis: {
              tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200,
              title: { text: mode === 'lost_sales' ? 'Lost Sales' : 'Backorder', font: { size: 9 } },
            },
            yaxis2: {
              overlaying: 'y', side: 'right', range: [0, 1], tickformat: '.0%',
              tickfont: { size: 9, family: FONT_MONO, color: '#27AE60' },
              title: { text: 'Fill Rate', font: { size: 9, color: '#27AE60' } },
              gridcolor: 'transparent', showgrid: false,
            },
          }}
          config={PLOTLY_CONFIG}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </Box>
    </Box>
  )
}
